/**
 * Componente para gestionar y mostrar reclamos de seguros
 * @description Maneja la visualización, filtrado y exportación de reclamos
 */
import { Component, ViewChild } from '@angular/core';
import { InsuranceTableComponent } from '../../../componentes/insurance-table/insurance-table.component';
import { ExportarComponent } from '../../../componentes/exportar/exportar.component';
import { CamposComponent } from '../../../componentes/campos/campos.component';
import { TableConfig } from '../../../interfaces/table-config.interface';
import { ReclamosFilter } from '../../../interfaces/filters.interfaces';
import { ApiResponse } from '../../../interfaces/common.interfaces';
import { AuthService } from '../../../services/auth.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import {
  ClaimResponse,
  ClaimsData,
  ClaimsRequest,
  ClaimOrderRequest,
} from '../../../interfaces/reclamaciones.interface';
import { ReclamacionesService } from '../../../services/reclamaciones.service';
import { HttpResponse } from '@angular/common/http';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';

@Component({
  selector: 'app-reclamos',
  standalone: true,
  imports: [
    InsuranceTableComponent,
    ExportarComponent,
    CamposComponent,
    CommonModule,
  ],
  templateUrl: './reclamos.component.html',
  styleUrl: './reclamos.component.css',
})
export class ReclamosComponent {
  /** Almacena los datos de reclamos a mostrar */
  insuranceData: ClaimResponse[] = [];
  /** Mensaje mostrado cuando no hay datos */
  mensajeNoData: string = 'Selecciona los campos que deseas buscar';
  /** Estado de error en las peticiones */
  errorStatus?: number;
  /** Fecha de corte para filtrado */
  fechaCorte?: Date;
  /** Indica si se encontraron resultados para imprimir */
  isFoundPrint: boolean = false;
  /** Título del componente */
  title: string = '';
  /** Fecha inicial del rango de búsqueda */
  fecha1?: Date;
  /** Fecha final del rango de búsqueda */
  fecha2?: Date;
  pageNumber: number = 1;
  pageSize: number = 5;
  totalRecordsFromServer: number = 0;
  totalPagesFromServer: number = 0;
  /**
   * Totales del servidor para la tabla. Usa `totalIncurrido` como total de la columna Monto.
   */
  tableTotals?: { [key: string]: number };
  private lastFilters?: ReclamosFilter;

  /** Número de evento para órdenes */
  eventNumber: string = '';

  /** Configuración de la tabla principal */
  tableConfig: TableConfig = {
    columns: [
      {
        key: 'claimNumber',
        label: 'Reclamo',
        sortable: true,
      },
      {
        key: 'policyNumber',
        label: 'Póliza',
        sortable: true,
        type: 'poliza',
      },
      {
        key: 'claimDate',
        label: 'Fecha',
        sortable: true,
      },
      {
        key: 'insuredName',
        label: 'Asegurado',
        sortable: true,
      },
      {
        key: 'claimantName',
        label: 'Reclamante',
        sortable: true,
        type: 'text',
      },
      {
        key: 'claimantTypeDescription',
        label: 'Tipo Reclamante',
        sortable: true,
        type: 'text',
      },

      {
        key: 'incurredAmount',
        label: 'Monto',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'status',
        label: 'Estado',
        sortable: true,
        type: 'text',
      },
      {
        key: 'actions',
        label: 'Ordenes',
        type: 'actions',
      },
    ],
    showTotals: true,
    totalFields: ['incurredAmount'],
  };

  /** Configuración de la tabla para exportación */
  tableConfigExport: TableConfig = {
    columns: [
      {
        key: 'claimNumber',
        label: 'Reclamo',
        sortable: true,
      },
      {
        key: 'policyNumber',
        label: 'Póliza',
        sortable: true,
        type: 'poliza',
      },
      {
        key: 'claimDate',
        label: 'Fecha',
        sortable: true,
      },
      {
        key: 'insuredName',
        label: 'Asegurado',
        sortable: true,
      },
      {
        key: 'claimantName',
        label: 'Reclamante',
        sortable: true,
        type: 'text',
      },
      {
        key: 'claimantTypeDescription',
        label: 'Tipo Reclamante',
        sortable: true,
        type: 'text',
      },
      {
        key: 'eventDescription',
        label: 'Evento',
        sortable: false,
        type: 'text',
      },
      {
        key: 'incurredAmount',
        label: 'Monto',
        sortable: true,
        type: 'currency',
      },
      {
        key: 'status',
        label: 'Estado',
        sortable: true,
        type: 'text',
      },
    ],
    showTotals: true,
    totalFields: ['incurredAmount'],
  };

  /** Referencia al componente de tabla */
  @ViewChild(InsuranceTableComponent) tablaComponent!: InsuranceTableComponent;

  /** Suscripción actual a peticiones */
  private currentSubscription?: Subscription;

  /**
   * Constructor del componente
   * @param reclamacionesService Servicio para gestionar reclamos
   * @param authService Servicio de autenticación
   */
  constructor(
    private reclamacionesService: ReclamacionesService,
    private authService: AuthService,
    private gAnalytics: GoogleAnalyticsService
  ) {
    this.title = 'Mis Reclamos';
  }

  opcionesSelectFilter = [
    { id: 'reclamo', nombre: 'Reclamo' },
    { id: 'poliza', nombre: 'Póliza' },
    { id: 'codigo', nombre: 'Código' },
    { id: 'cedula', nombre: 'Cédula' },
    { id: 'rnc', nombre: 'RNC' },
    { id: 'nombre', nombre: 'Nombre' },
  ];

  /**
   * Maneja los cambios en los filtros de búsqueda
   * @param filters Filtros aplicados por el usuario
   */
  onFilterChange(filters: ReclamosFilter): void {
    this.insuranceData = [];
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
    }

    if (Object.keys(filters).length === 0) {
      this.insuranceData = [];
      this.tableTotals = undefined;
      this.mensajeNoData = 'Selecciona los campos que deseas buscar';
      this.errorStatus = undefined;
      return;
    }
    this.fecha1 = filters.dateFrom;
    this.fecha2 = filters.dateTo;
    this.lastFilters = filters;
    this.pageNumber = 1;
    const request: ClaimsRequest = {
      ...(filters.dateFrom && { DateFrom: filters.dateFrom }),
      ...(filters.dateTo && { DateTo: filters.dateTo }),
      ProducerNumber: this.authService.getProducerNumber() || 0,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      ...(filters?.policyNumber && { policyNumber: filters.policyNumber }),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
      ...(filters?.eventNumber && { EventNumber: filters.eventNumber })
    };

    if (filters.lobId) request.Lob = filters.lobId;
    if (filters.sLobId) request.Slob = filters.sLobId;
    if (filters.clientCode) request.ClientCode = filters.clientCode;
    this.currentSubscription = this.reclamacionesService
      .getClaims(request)
      .subscribe({
        next: (response: HttpResponse<ApiResponse<ClaimsData>>) => {
          if (response.status === 200) {
            this.insuranceData =
              response.body?.data.claims.map((item) => ({
                ...item,
                claimNumber: item.claimNumber,
                orders: [],
                selectedOrderId: 'all',
                actions: 'No disponible',
              })) || [];
            this.totalRecordsFromServer =
              response.body?.data?.pagination?.totalRecords || 0;
            this.totalPagesFromServer =
              response.body?.data?.pagination?.totalPages || 0;
            this.tableTotals = {
              incurredAmount: response.body?.data?.pagination?.totalIncurrido ?? 0,
            };
            this.errorStatus = undefined;
          } else if (response.status === 204) {
            this.insuranceData = [];
            this.tableTotals = undefined;
            this.mensajeNoData = 'No hay datos disponibles para mostrar';
          }
        },
        error: (error) => {
          console.error('Error al obtener los reclamos:', error);
          this.insuranceData = [];
          this.tableTotals = undefined;
          this.mensajeNoData =
            'No se han podido obtener los datos de los reclamos';
        },
      });
  }

  /**
   * Solicita al API la página seleccionada en la tabla
   * @param page Número de página a consultar
   */
  onPageChange(page: number): void {
    this.pageNumber = page;
    const filters = this.lastFilters;

    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
    }

    const request: ClaimsRequest = {
      ...(filters?.dateFrom && { DateFrom: filters.dateFrom }),
      ...(filters?.dateTo && { DateTo: filters.dateTo }),
      ProducerNumber: this.authService.getProducerNumber() || 0,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      ...(filters?.policyNumber && { policyNumber: filters.policyNumber }),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
      ...(filters?.eventNumber && { EventNumber: filters.eventNumber }),
    };

    if (filters?.lobId) request.Lob = filters.lobId;
    if (filters?.sLobId) request.Slob = filters.sLobId;
    if (filters?.clientCode) request.ClientCode = filters.clientCode;

    this.currentSubscription = this.reclamacionesService
      .getClaims(request)
      .subscribe({
        next: (response: HttpResponse<ApiResponse<ClaimsData>>) => {
          if (response.status === 200) {
            this.insuranceData =
              response.body?.data.claims.map((item) => ({
                ...item,
                claimNumber: item.claimNumber,
                orders: [],
                selectedOrderId: 'all',
                actions: 'No disponible',
              })) || [];
            this.totalRecordsFromServer =
              response.body?.data?.pagination?.totalRecords || 0;
            this.totalPagesFromServer =
              response.body?.data?.pagination?.totalPages || 0;
            this.tableTotals = {
              incurredAmount: response.body?.data?.pagination?.totalIncurrido ?? 0,
            };
            this.errorStatus = undefined;
          } else if (response.status === 204) {
            this.insuranceData = [];
            this.tableTotals = undefined;
            this.mensajeNoData = 'No hay datos disponibles para mostrar';
          }
        },
        error: (error) => {
          console.error('Error al obtener los reclamos:', error);
          this.insuranceData = [];
          this.tableTotals = undefined;
          this.mensajeNoData =
            'No se han podido obtener los datos de los reclamos';
        },
      });
  }

  /**
   * Exporta todos los registros del reporte según los filtros actuales
   * Consulta el total de reclamos al API y los usa solo para la exportación
   * @param formato Formato de exportación ('xlsx', 'pdf', etc)
   */
  onExportar(formato: string): void {
    if (!this.tablaComponent || this.totalRecordsFromServer === 0) {
      return;
    }

    const headerInfo = {
      titulo: this.title,
      fecha1: this.fecha1!,
      fecha2: this.fecha2,
      lob: this.getLobName(),
      slob: this.getSlobName(),
      tableConfig: this.tableConfig,
    };

    this.gAnalytics.trackEvent('cartera_exportar', {
      page: '/cartera',
      format: formato,
    });

    const filters = this.lastFilters;
    const request: ClaimsRequest = {
      ...(filters?.dateFrom && { DateFrom: filters.dateFrom }),
      ...(filters?.dateTo && { DateTo: filters.dateTo }),
      ProducerNumber: this.authService.getProducerNumber() || 0,
      pageNumber: 1,
      pageSize: this.totalRecordsFromServer,
      ...(filters?.policyNumber && { policyNumber: filters.policyNumber }),
      ...(filters?.clientCode && { ClientCode: filters.clientCode }),
      ...(filters?.eventNumber && { EventNumber: filters.eventNumber }),
    };

    if (filters?.lobId) request.Lob = filters.lobId;
    if (filters?.sLobId) request.Slob = filters.sLobId;
    if (filters?.clientCode) request.ClientCode = filters.clientCode;

    this.reclamacionesService.getClaims(request).subscribe({
      next: (response: HttpResponse<ApiResponse<ClaimsData>>) => {
        if (response.status !== 200) {
          return;
        }

        const allData =
          response.body?.data.claims.map((item) => ({
            ...item,
            claimNumber: item.claimNumber,
            orders: [],
            selectedOrderId: 'all',
            actions: 'No disponible',
          })) || [];
        const dataOriginal = this.tablaComponent.data;
        this.tablaComponent.data = allData;
        this.tablaComponent.exportarDatos(formato, headerInfo);
        this.tablaComponent.data = dataOriginal;
      },
      error: (error) => {
        console.error('Error al obtener los reclamos para exportar:', error);
      },
    });
  }

  /** Referencia al componente de campos */
  @ViewChild(CamposComponent) camposComponent!: CamposComponent;

  /**
   * Obtiene el nombre del ramo seleccionado
   * @returns Nombre del ramo o cadena vacía
   */
  public getLobName(): string {
    const selectedLob = this.camposComponent?.lobs.find(
      (lob) => lob.lobId === Number(this.camposComponent?.selectedLob)
    );
    return selectedLob?.lobName || '';
  }

  /**
   * Obtiene el nombre del subramo seleccionado
   * @returns Nombre del subramo o cadena vacía
   */
  public getSlobName(): string {
    const selectedSLob = this.camposComponent?.filteredSLobs.find(
      (slob) => slob.sLobId === Number(this.camposComponent?.selectedSLob)
    );
    return selectedSLob?.sLobName || '';
  }

  /**
   * Maneja la impresión de órdenes
   * @param data Objeto con el reclamo y el ID de la orden
   */
  onPrintOrder(data: { item: ClaimResponse; orderId: string }) {
    this.eventNumber = String(data.item.claimNumber);
    const request: ClaimOrderRequest = {
      EventNumber: data.item.claimNumber,
    };

    this.reclamacionesService.getClaimOrders(request).subscribe({
      next: (response) => {
        if (data.orderId === 'all') {
          response.data.forEach((order) => {
            window.open(order.orderUrl, '_blank');
          });
        } else {
          const selectedOrder = response.data.find(
            (order) => order.orderId === parseInt(data.orderId)
          );
          if (selectedOrder) {
            window.open(selectedOrder.orderUrl, '_blank');
          }
        }
      },
      error: (error) => {
        if (error.status === 404) {
          this.isFoundPrint = true;
        }
        console.error('Error al obtener las órdenes:', error);
      },
    });
  }

  /**
   * Carga las órdenes asociadas a un reclamo
   * @param item Reclamo del cual cargar las órdenes
   */
  loadOrders(item: ClaimResponse) {
    if (!item.orders || item.orders.length === 0) {
      const request: ClaimOrderRequest = {
        EventNumber: item.claimNumber,
      };

      this.reclamacionesService.getClaimOrders(request).subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            item.orders = response.data;
            item.selectedOrderId = 'all';
          }
        },
        error: (error) => {
          console.error('Error loading orders:', error);
          if (error.status === 404) {
            this.isFoundPrint = true;
          }
        },
      });
    }
  }
}
